/**
 * TD Analytics Events Web Component
 * Pushes the Capture Health funnel to the GTM dataLayer.
 *
 * Checkout leaves Shopify entirely, so Shopify analytics sees nothing past the
 * cart. This component emits the three funnel steps instead:
 *   add_to_cart    - from the PUB_SUB_EVENTS.cartUpdate signal
 *   begin_checkout - from 'td-capture-health:checkout-started'
 *   purchase       - from 'td-capture-health:checkout-complete'
 *
 * Loaded as an asset rather than a {% javascript %} block because it subscribes
 * to pubsub at connect time, so it must be ordered after pubsub.js. Same reason
 * and same pattern as td-kit-addon-validator.js.
 *
 * NEVER push customer name, email or phone. These are prescription purchases;
 * identifiable health data must not reach GA4, Meta or any other tag.
 *
 * Usage in theme.liquid:
 * <td-analytics-events></td-analytics-events>
 */

if (!customElements.get('td-analytics-events')) {
  class TDAnalyticsEvents extends HTMLElement {
    /* cartUpdate is published for quantity edits and removals too (cart.js,
       quick-order-list.js). Only these sources are genuine adds. */
    static ADD_TO_CART_SOURCES = ['product-form', 'td-showcase', 'td-kit-upsell', 'td-kit-upsell-drawer'];

    /* Capture Health prices in decimal dollars, matching what we send them.
       If a future payload switches to cents the total lands ~100x off, so warn
       rather than silently poisoning revenue reporting. */
    static UNIT_MISMATCH_RATIO = 50;

    constructor() {
      super();
      this.cartUpdateUnsubscriber = null;
      this.reportedOrderIds = new Set();
      this.lastCheckoutTotal = null;

      this.BOUND_CHECKOUT_STARTED = this.onCheckoutStarted.bind(this);
      this.BOUND_CHECKOUT_COMPLETE = this.onCheckoutComplete.bind(this);
    }

    connectedCallback() {
      this.subscribeToCartUpdates();
      document.addEventListener('td-capture-health:checkout-started', this.BOUND_CHECKOUT_STARTED);
      document.addEventListener('td-capture-health:checkout-complete', this.BOUND_CHECKOUT_COMPLETE);
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
      document.removeEventListener('td-capture-health:checkout-started', this.BOUND_CHECKOUT_STARTED);
      document.removeEventListener('td-capture-health:checkout-complete', this.BOUND_CHECKOUT_COMPLETE);
    }

    /**
     * Push an event to the GTM dataLayer.
     * @param {string} event - GA4 event name
     * @param {Object} ecommerce - GA4 ecommerce payload
     */
    push(event, ecommerce) {
      try {
        window.dataLayer = window.dataLayer || [];
        /* Null the previous ecommerce object so GA4 does not merge stale keys. */
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event, ecommerce });
      } catch (error) {
        console.error('TD Analytics: Failed to push to dataLayer', error);
      }
    }

    /**
     * Active store currency. Prefers Shopify's runtime value, which follows
     * the currency selector, then the Liquid-set data attribute, so a
     * multi-currency store never silently reports the wrong code.
     * @returns {string} ISO 4217 currency code
     */
    getCurrency() {
      return window.Shopify?.currency?.active || this.dataset.currency || 'USD';
    }

    subscribeToCartUpdates() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.onCartUpdate(event);
        });
      }
    }

    /**
     * Emit add_to_cart, but only for real adds.
     * @param {Object} event - cartUpdate payload
     */
    onCartUpdate(event) {
      if (!event || !TDAnalyticsEvents.ADD_TO_CART_SOURCES.includes(event.source)) {
        return;
      }

      const items = this.extractAddedItems(event.cartData);
      if (items.length === 0) return;

      const value = items.reduce((total, item) => total + item.price * item.quantity, 0);

      this.push('add_to_cart', {
        currency: this.getCurrency(),
        value: this.round(value),
        items: items,
      });
    }

    /**
     * The /cart/add.js response is a single line item when one item is added and
     * an { items: [] } wrapper when several are. Handle both.
     * @param {Object} cartData - Response from /cart/add.js
     * @returns {Array<Object>} GA4 item objects
     */
    extractAddedItems(cartData) {
      if (!cartData) return [];

      const lineItems = Array.isArray(cartData.items) ? cartData.items : [cartData];

      return lineItems
        .filter((lineItem) => lineItem && lineItem.id)
        .map((lineItem) => ({
          item_id: String(lineItem.product_id || lineItem.id),
          item_name: lineItem.product_title || lineItem.title || '',
          item_variant: lineItem.variant_title || '',
          price: this.round((lineItem.final_price ?? lineItem.price ?? 0) / 100),
          quantity: lineItem.quantity || 1,
        }));
    }

    /**
     * Emit begin_checkout. Dispatched by td-capture-health-checkout only after
     * the API returns a URL, so failed submissions are excluded.
     * @param {CustomEvent} evt - detail: { value, items }
     */
    onCheckoutStarted(evt) {
      const { value, items } = evt.detail || {};

      /* Retained to sanity-check the completion amount against what we sent. */
      this.lastCheckoutTotal = typeof value === 'number' ? value : null;

      this.push('begin_checkout', {
        currency: this.getCurrency(),
        value: this.round(value || 0),
        items: items || [],
      });
    }

    /**
     * Emit purchase from the Capture Health completion.
     * td-iframe-modal has already verified the message origin and source and
     * filtered for CHECKOUT_COMPLETE, so the detail can be trusted.
     * @param {CustomEvent} evt - detail: { customer, order, origin }
     */
    onCheckoutComplete(evt) {
      const order = evt.detail?.order || {};
      const orderId = order.orderId ? String(order.orderId) : '';

      if (!orderId) {
        console.warn('TD Analytics: CHECKOUT_COMPLETE received without an orderId, skipping');
        return;
      }

      /* The message may repeat; one purchase per order only. */
      if (this.reportedOrderIds.has(orderId)) return;
      this.reportedOrderIds.add(orderId);

      const amount = typeof order.amount === 'number' ? order.amount : parseFloat(order.amount);
      const value = Number.isFinite(amount) ? amount : 0;

      this.warnOnUnitMismatch(value);

      /* evt.detail.customer is deliberately discarded - see file header. */
      this.push('purchase', {
        transaction_id: orderId,
        currency: this.getCurrency(),
        value: this.round(value),
      });
    }

    /**
     * Capture Health's postMessage contract is undocumented, so guard against a
     * silent units change by comparing against the total we sent at checkout.
     * @param {number} value - Amount from the completion message
     */
    warnOnUnitMismatch(value) {
      if (!this.lastCheckoutTotal || !value) return;

      const ratio = value / this.lastCheckoutTotal;
      if (ratio > TDAnalyticsEvents.UNIT_MISMATCH_RATIO) {
        console.warn(
          `TD Analytics: purchase amount ${value} is ${Math.round(ratio)}x the ${this.lastCheckoutTotal} sent at checkout. ` +
            'Capture Health may have switched to cents - GA4 revenue is likely wrong.'
        );
      }
    }

    /**
     * @param {number} amount
     * @returns {number} Amount rounded to two decimals
     */
    round(amount) {
      return Math.round((amount + Number.EPSILON) * 100) / 100;
    }
  }

  customElements.define('td-analytics-events', TDAnalyticsEvents);
}
